/**
 * FERP Portal - Patient Safety Page
 *
 * LIFE-CRITICAL: Patient safety monitoring and incident management.
 * This page is only visible when the tenant has life-critical subgraphs enabled.
 *
 * DATA-DRIVEN: Entity types and configuration come from SubgraphMetadata.
 * NO HARDCODED mock data - all data from GraphQL repositories.
 *
 * FERP Principles:
 * - ZERO TOLERANCE FOR FAILURE in patient safety
 * - Dual approval for safety-critical actions
 * - Complete audit trail
 * - Data-driven: All entity types from SubgraphMetadata
 */

import React, { useState, useMemo } from 'react';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Badge,
  Button,
  Tabs,
  Alert,
  Loader,
  Center,
  ThemeIcon,
  SimpleGrid,
  RingProgress,
} from '@mantine/core';
import {
  IconHeartbeat,
  IconShieldCheck,
  IconPlus,
  IconDatabase,
  IconAlertTriangle,
} from '@tabler/icons-react';
import { useTenantId } from '@/auth';
import { useTenantSubgraphConfig } from '@/hooks/useTenantSubgraphConfig';
import { DataDrivenEntityList } from '@/components';
import { getIcon } from '@/utils/iconMap';

export function PatientSafetyDashboard(): React.ReactElement {
  const [activeTab, setActiveTab] = useState<string | null>('dashboard');

  const tenantId = useTenantId();
  const {
    config,
    loading: configLoading,
    error: configError,
  } = useTenantSubgraphConfig(tenantId);

  // Get all life-critical subgraphs that contribute to patient safety
  const lifeCriticalSubgraphs = useMemo(() => {
    if (!config?.subgraphs) return [];
    return config.subgraphs.filter((s) => s.isLifeCritical);
  }, [config]);

  // Get all entity types that are life-critical (for safety monitoring)
  const safetyEntityTypes = useMemo(() => {
    if (!config?.allEntityTypes) return [];
    return config.allEntityTypes.filter((e) => e.isLifeCritical);
  }, [config]);

  // Check if there are any life-critical subgraphs
  const hasLifeCritical = lifeCriticalSubgraphs.length > 0;

  // Loading state
  if (configLoading) {
    return (
      <Container size="xl" py="xl">
        <Center h={400}>
          <Stack ta="center" gap="md">
            <Loader size="lg" />
            <Text c="dimmed">Loading safety configuration...</Text>
          </Stack>
        </Center>
      </Container>
    );
  }

  // Error state
  if (configError) {
    return (
      <Container size="xl" py="xl">
        <Alert color="red" title="Configuration Error" icon={<IconHeartbeat />}>
          Failed to load safety configuration: {configError.message}
        </Alert>
      </Container>
    );
  }

  // No life-critical subgraphs enabled
  if (!hasLifeCritical) {
    return (
      <Container size="xl" py="xl">
        <Alert color="yellow" title="No Life-Critical Subgraphs" icon={<IconHeartbeat />}>
          Your tenant does not have any life-critical subgraphs enabled.
          Patient safety features require FHIR or other healthcare subgraphs.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" ta="left">
          <div>
            <Group gap="sm">
              <ThemeIcon size="lg" variant="filled" color="red">
                <IconHeartbeat size={20} />
              </ThemeIcon>
              <div>
                <Title order={2}>Patient Safety</Title>
                <Text c="dimmed" size="sm">
                  Life-critical safety monitoring and incident management
                </Text>
              </div>
            </Group>
          </div>
          <Group>
            <Badge color="red" variant="filled" size="lg">
              LIFE-CRITICAL
            </Badge>
            <Button leftSection={<IconPlus size={16} />} color="red">
              Report Incident
            </Button>
          </Group>
        </Group>

        {/* Life-Critical Warning */}
        <Alert
          icon={<IconShieldCheck size={16} />}
          title="Life-Critical System"
          color="red"
          variant="filled"
        >
          This system monitors patient safety events. All actions are logged and may require
          dual approval. Sentinel events trigger immediate escalation.
        </Alert>

        {/* Statistics Cards - Data-Driven from life-critical subgraphs */}
        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
          <Card withBorder padding="md" style={{ borderLeft: '4px solid var(--mantine-color-green-6)' }}>
            <Group justify="space-between">
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  Safety Score
                </Text>
                <Text size="xl" fw={700} c="green">
                  --
                </Text>
              </div>
              <RingProgress
                size={50}
                thickness={5}
                sections={[{ value: 0, color: 'green' }]}
              />
            </Group>
          </Card>

          <Card withBorder padding="md" style={{ borderLeft: '4px solid var(--mantine-color-red-6)' }}>
            <Group justify="space-between">
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  Open Incidents
                </Text>
                <Text size="xl" fw={700} c="red">
                  --
                </Text>
              </div>
              <ThemeIcon size="lg" variant="light" color="red">
                <IconAlertTriangle size={20} />
              </ThemeIcon>
            </Group>
          </Card>

          <Card withBorder padding="md">
            <Group justify="space-between">
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  Life-Critical Subgraphs
                </Text>
                <Text size="xl" fw={700}>
                  {lifeCriticalSubgraphs.length}
                </Text>
              </div>
              <ThemeIcon size="lg" variant="light" color="red">
                <IconHeartbeat size={20} />
              </ThemeIcon>
            </Group>
          </Card>

          <Card withBorder padding="md">
            <Group justify="space-between">
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                  Monitored Entities
                </Text>
                <Text size="xl" fw={700}>
                  {safetyEntityTypes.length}
                </Text>
              </div>
              <ThemeIcon size="lg" variant="light" color="blue">
                <IconShieldCheck size={20} />
              </ThemeIcon>
            </Group>
          </Card>
        </SimpleGrid>

        {/* Tabs - Generated from life-critical entity types */}
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="dashboard" leftSection={<IconHeartbeat size={14} />}>
              Dashboard
            </Tabs.Tab>
            {safetyEntityTypes.map((entityType) => {
                const EntityIcon = getIcon(entityType.icon ?? 'IconShieldCheck');
              return (
                <Tabs.Tab
                  key={entityType.graphqlType}
                  value={entityType.graphqlType.toLowerCase()}
                  leftSection={<EntityIcon size={14} />}
                >
                  {entityType.pluralName}
                </Tabs.Tab>
              );
            })}
          </Tabs.List>

          {/* Dashboard Tab */}
          <Tabs.Panel value="dashboard" pt="md">
            <Stack gap="md">
              {/* Life-Critical Subgraphs */}
              <Card withBorder>
                <Text fw={600} mb="md">Life-Critical Subgraphs</Text>
                <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                  {lifeCriticalSubgraphs.map((subgraph) => {
                    const SubgraphIcon = getIcon(subgraph.icon);
                    return (
                      <Card key={subgraph.subgraphId} withBorder padding="sm">
                        <Group>
                          <ThemeIcon size="lg" variant="light" color={subgraph.color}>
                            <SubgraphIcon size={18} />
                          </ThemeIcon>
                          <div>
                            <Text fw={500}>{subgraph.displayName}</Text>
                            <Text size="xs" c="dimmed">
                              Life-critical subgraph
                            </Text>
                          </div>
                        </Group>
                        <Group gap="xs" mt="xs">
                          {subgraph.complianceFrameworks.slice(0, 3).map((fw: string) => (
                            <Badge key={fw} size="xs" variant="outline">
                              {fw}
                            </Badge>
                          ))}
                        </Group>
                      </Card>
                    );
                  })}
                </SimpleGrid>
              </Card>

              {/* Life-Critical Entity Types */}
              <Card withBorder>
                <Text fw={600} mb="md">Life-Critical Entity Types</Text>
                <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="md">
                  {safetyEntityTypes.map((entityType) => (
                    <Button
                      key={entityType.graphqlType}
                      variant="light"
                      color="red"
                      leftSection={<IconShieldCheck size={16} />}
                      onClick={() => setActiveTab(entityType.graphqlType.toLowerCase())}
                    >
                      {entityType.pluralName}
                    </Button>
                  ))}
                </SimpleGrid>
              </Card>

              {/* Data-Driven Info */}
              <Alert color="blue" variant="light" icon={<IconDatabase />}>
                <Text size="sm">
                  This page is <strong>data-driven</strong>. Life-critical entity types are
                  automatically detected from SubgraphMetadata. Add new safety-monitored entities
                  by configuring them as life-critical in the backend.
                </Text>
              </Alert>
            </Stack>
          </Tabs.Panel>

          {/* Entity Type Tabs - Generated from life-critical entity types */}
          {safetyEntityTypes.map((entityType) => (
            <Tabs.Panel
              key={entityType.graphqlType}
              value={entityType.graphqlType.toLowerCase()}
              pt="md"
            >
              <DataDrivenEntityList
                entityName={entityType.name}
                onRowClick={(row) => {
                  console.log('View entity:', row);
                }}
                onCreateClick={() => {
                  console.log('Create new:', entityType.name);
                }}
                onEditClick={(row) => {
                  console.log('Edit entity:', row);
                }}
                onArchiveClick={(row) => {
                  console.log('Archive entity:', row);
                }}
              />
            </Tabs.Panel>
          ))}
        </Tabs>
      </Stack>
    </Container>
  );
}

export default PatientSafetyDashboard;
